import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'


import { MapHandoffSheet } from '@/features/map/map-handoff-sheet'
import type { ActivityDetail } from '../api'
import { ActivityMapPreview } from './activity-map-preview'

interface LocationSectionProps {
  activity: ActivityDetail
  canViewSensitive: boolean
}

/**
 * Mask-aware map section. Creator/confirmed viewers see the exact pin and can
 * open directions from it. Everyone else sees only the 300 m privacy circle
 * returned by the API (D-085); no pin is drawn because the coordinates are an
 * area centre, not a venue. Do not round the received point again.
 */
export function LocationSection({
  activity,
  canViewSensitive,
}: LocationSectionProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const [handoffOpen, setHandoffOpen] = useState(false)
  // The API already masks the coordinates with the privacy area, so both
  // viewers render the point exactly as received (D-085).
  const latitude = activity.latitude
  const longitude = activity.longitude
  const hasCoordinates = latitude != null && longitude != null
  const area =
    (canViewSensitive
      ? activity.locationName?.trim()
      : activity.city?.trim() || activity.locationName?.trim()) ||
    activity.city?.trim() ||
    activity.title

  return (
    <section aria-labelledby={headingId}>
      <h2
        id={headingId}
        className="font-head text-[18px] font-extrabold text-deep"
      >
        {canViewSensitive
          ? t('activityDetail.map.exactTitle')
          : t('activityDetail.map.coarseTitle')}
      </h2>

      {hasCoordinates ? (
        <div className="mt-3">
          <ActivityMapPreview
            latitude={latitude}
            longitude={longitude}
            exact={canViewSensitive}
            areaLabel={area}
            color={activity.categoryColor}
            onOpenDirections={
              canViewSensitive ? () => setHandoffOpen(true) : undefined
            }
          />
        </div>
      ) : (
        <p className="mt-3 rounded-md bg-[var(--mint)] p-3 text-sm text-muted">
          {t('activityDetail.map.unavailable')}
        </p>
      )}

      {!canViewSensitive ? (
        <p className="mt-2 text-xs leading-relaxed font-semibold text-muted">
          {t('activityDetail.map.coarseBody')}
        </p>
      ) : null}

      {canViewSensitive ? (
        <MapHandoffSheet
          open={handoffOpen}
          onClose={() => setHandoffOpen(false)}
          title={activity.title}
          latitude={latitude}
          longitude={longitude}
        />
      ) : null}
    </section>
  )
}
